import { remote, supertypeClass, property } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types';
import { WorkflowUtils } from '../workflow/WorkflowUtils';
import { RemoteDocService } from '../webservice/RemoteDocService';
import { Policy } from '../customer/Policy';
import { Customer } from '../customer/Customer';
import { AuthenticatingController } from '../AuthenticatingController';
import { PutObjectOutput } from 'aws-sdk/clients/s3';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

const MODULE = 'RemoteDocServiceController';

interface ControllerWithCustomer extends AuthenticatingController {
  customer?: Customer;
}

@supertypeClass({ toClient: true, toServer: true })
export class RemoteDocServiceController extends AppController {
  @property()
  controller: AuthenticatingController;

  get customer(): Customer {
    return (<ControllerWithCustomer>this.controller).customer || null;
  }

  static new(controller: AuthenticatingController): RemoteDocServiceController {
    return new RemoteDocServiceController().init(controller);
  }

  init(controller: AuthenticatingController): this {
    this.controller = controller;
    return this;
  }

  validateServerCall() {
    if (this.controller.isLoggedIn()) {
      return true;
    }

    LoggerSingleton.getInstance().error(
      {
        module: MODULE,
        function: 'validateServerCall',

        category: Category.request,
      },
      'User is not authenticated'
    );
    return false;
  }

  /**
   * @param base64 - base64 of the file
   * @param nameFull
   * @param policy - policy object of type Policy
   * @param appType - visa, I797
   */
  @remote({ on: 'server' })
  uploadFile(base64: string, nameFull: string, policy: Policy, appType: string): Promise<void | PutObjectOutput> {
    if (RemoteDocService.customerHasPermissions(this.customer, policy)) {
      return RemoteDocService.uploadFile(this.amorphic, base64, nameFull, policy, appType);
    }
  }

  /**
   * check if doc already uploaded, if so return true, else return false
   * @param policy - policy object of type Policy
   * @param appType - visa, I797,
   * @param getLatest if true, will get the latest from Policy Doc if multiple files.
   * @returns the body of the object that got uploaded .
   */
  @remote({ on: 'server' })
  async downloadObjectAndCleanUp(policy: Policy, appType: string, getLatest: boolean): Promise<object> {
    if (RemoteDocService.customerHasPermissions(this.customer, policy)) {
      return RemoteDocService.downloadObjectAndCleanUp(this.amorphic, policy, appType, getLatest);
    }
  }

  /**
   * For any reUploads, mark the existing docs as archive for that particular type of document to avoid redundancy.
   * @param policy - policy object of type Policy
   * @param appType - visa, I797
   */
  @remote({ on: 'server' })
  async deleteFile(policy: Policy, appType: string): Promise<boolean> {
    if (RemoteDocService.customerHasPermissions(this.customer, policy)) {
      return WorkflowUtils.archivePolicyDocs(policy, appType, this.amorphic);
    }
  }
}
